import React, {createRef, useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import axios from "axios";
import moment from "moment";
import __ from "lodash";
import { v4 as uuidv4 } from 'uuid';

import MaterialTable from "@material-table/core";
import {
  TablePagination,
  IconButton, Tooltip, Button, Icon, useTheme,
} from "@material-ui/core";

import SystemContext from "../../../SystemContext";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import PlayIcon from "@material-ui/icons/PlayCircleFilled";
import { getApiSitePrefix } from "../../../functions/SiteDefaults";
import { getTemplate, getDefaultValues } from "../../../functions/TemplateHelperFunctions"
import { defaultComponents } from '../../../controls/Material-Table-Components';
import { ComboSelect, LightTooltip } from '../../../controls';

import AirportNameTooltip from "../../Airports/components/AirportNameTooltip";
import CompanyUuidLookup from "../../Companies/components/CompanyUuidLookup";
import HandlingOrdersValidator from "../validators/HandlingOrdersListValidator";
import SendHandlingOrderDialog from "./HandlingOrderDialogs/SendHandlingOrderDialog";
import AddHandlingOrderDialog from "./HandlingOrderDialogs/AddHandlingOrderDialog";

import { useHandlingOrders, useMoveOrderLinesToNewHandlingOrder} from "../../../services/handling";
import useColour, {getStatusColour} from '../../../services/settings/useColour';

import { fetch as fetchDefaultServiceTypes } from "../../../services/settings/useAirportServiceTypes";
import { fetch as fetchTripLegServices } from "../../../services/trips/useTripLegServices";
import { fetch as fetchComboValues } from "../../../services/settings/useComboValues";

const apiSitePrefix = getApiSitePrefix();
const dateFormat = "DD/MM/YYYY";
const formatChars={
  "U": "[A-Z]",
  "A": "[A-Z0-9]",
  "a": "[A-Za-z0-9]",
  "i": "[A-Za-z0-9\\-]",
  "9": "[0-9]",
  "D": "[0-9.]",
  "-": "-",
  "x": "."
}
const isHTML = (str) => !(str || '')
  .replace(/[\n\r]/g, '')
  // replace html tag with content
  .replace(/<([^>]+?)([^>]*?)>(.*?)<\/\1>/ig, '')
  // remove remaining self closing tags
  .replace(/(<([^>]+)>)/ig, '')
  // remove extra space at start and end
  .trim();


const HandlingOrdersToReviewList = (_props) => {
  const props = {
    ..._props
  }
  const {classes} = props;

  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();

  const [state] = useState({
    canEdit: props.canEdit,
  });
  const [addDialogStates, setAddDialogStates] = useState({
    showDialog: false,
    record: {},
    parentRecord: null,
    title: 'New Handling Order'
  });
  const [sendDialogStates, setSendDialogStates] = useState({
    showDialog: false,
    record: {},
    parentRecord: null,
    title: 'Send'
  });
  const [defaultValues, setDefaultValues] = useState({
    defaultTemplate: null,
    defaultAttachment: null,
  });

  const year = props.year;
  const month = props.month;
  const required = props.required;

  const tableRef = createRef({state: {lastEditingRow: false}});

  const queryClient = useQueryClient();
  const extraKeys = [year, month];
  if (required === "review")
    extraKeys.push("review");

  const handlingOrders = useHandlingOrders({tableRef, queryClient, extraKeys});
  const handleMoveOrderLinesToNewHandlingOrder = useMoveOrderLinesToNewHandlingOrder();

  const colours = useColour({code: "HANDLING ORDERS TEXT"});
  const backgroundColours = useColour({code: "HANDLING ORDERS BACKGROUND"});

  const refreshList = async () => {
    queryClient.invalidateQueries(["HandlingOrders"]);
  }


  const closeAddOrder = () => {
    setAddDialogStates({
      ...addDialogStates,
      showDialog: false,
      record: {},
      parentRecord: null,
      title: 'New Handling Order'
    });
  }

  const handleCancelRequirement = async (selectedRecords) => {
    try {
      SystemData.toggleLoadingProgress(true, "Removing cancelled requirements");
      const response = await axios.put(`${apiSitePrefix}/handlingorders/cancelRequirements`, selectedRecords);
      SystemData.toggleLoadingProgress(false);
      if (!response.data) {
        SystemData.alert("Error", `Could not remove cancelled requirements. Server is not responding`);
      } else if (response.data.success) {
        queryClient.invalidateQueries(["HandlingOrders", 0, 0 , "review"]);
        queryClient.invalidateQueries(["HandlingOrders", "Count", 0, 0 , "review"]);
      } else {
        SystemData.alert("Error", `Could not remove cancelled requirements. Server returned ${response.data.error}`);
      }
    } catch (err) {
      SystemData.toggleLoadingProgress(false);
      SystemData.alert("Error", `Could not remove cancelled requirements. Server is not responding`);
    }
  }

  const handleIgnoreChangesClick = async (selectedRecord) => {
    SystemData.confirm(
      `Ignore Changes`,
      `Are you sure you wish to ignore the changes to the flight?'`,
      [
        {icon: "check_circle", value: true, label: "Yes", options: {color:"primary"}},
        {icon: "cancel", value: false, label: "No", options: {color:"secondary"}}
      ],
      async (response) => {
        if (response) {
          try {
            SystemData.toggleLoadingProgress(true, "Ignoring changes");
            const response = await axios.put(`${apiSitePrefix}/handlingorders/ignoreChanges`, [selectedRecord]);
            SystemData.toggleLoadingProgress(false);
            if (!response.data) {
              SystemData.alert("Error", `Could not ignore changes. Server is not responding`);
            } else if (response.data.success) {
              queryClient.invalidateQueries(["HandlingOrders", 0, 0 , "review"]);
              queryClient.invalidateQueries(["HandlingOrders", "Count", 0, 0 , "review"]);
            } else {
              SystemData.alert("Error", `Could not ignore changes. Server returned ${response.data.error}`);
            }
          } catch (err) {
            SystemData.toggleLoadingProgress(false);
            SystemData.alert("Error", `Could not ignore changes. Server is not responding`);
          }
        }
      }
    );
  }

  const getPreSelectedServices = async () => {
    const preSelectedServices = []
    const presetServices = await fetchComboValues({queryClient, extraKeys: ["RAMP SERVICES", "SKELETON"], useCache: true})
    if (presetServices && Array.isArray(presetServices)) {
      presetServices.forEach(service => {
        if (__.has(service, "data") && __.has(service.data, "alwaysPreSelect") && service.data.alwaysPreSelect)
          preSelectedServices.push(service.code);
      });
    }
    return preSelectedServices;
  }

  const getSupplierAndTemplate = async (record) => {
    let companyUuid = null;
    let handlingOrderTemplateUuid = null;
    const defaultServiceTypes = await fetchDefaultServiceTypes({queryClient, extraKeys: [record.airportCode], useCache: false});
    if (defaultServiceTypes && Array.isArray(defaultServiceTypes)) {
      const rampHandlingService = __.find(defaultServiceTypes, {serviceCode: "RAMP HANDLING"});
      if (rampHandlingService && __.has(rampHandlingService, "companyUuid"))
      companyUuid = rampHandlingService.companyUuid;
      if (rampHandlingService && __.has(rampHandlingService, "orderTemplate"))
        handlingOrderTemplateUuid = rampHandlingService.orderTemplate;
    }

    return {companyUuid, handlingOrderTemplateUuid};
  }

  const getRequiredServices = async (record, companyUuid) => {
    const requiredServices = [];
    const tripLegServices = await fetchTripLegServices({queryClient, extraKeys: [record.tripLegUuid], useCache: false});
    if (tripLegServices && Array.isArray(tripLegServices)) {
      tripLegServices.forEach(service=> {
        if (service.isRequired && (service.serviceProviderUuid === companyUuid || !service.serviceProviderUuid) && !["CATERING", "FUELING"].includes(service.serviceCode))
          requiredServices.push(service.serviceCode);
      });
    }

    return requiredServices;
  }

  const handleAmendOrder = async (selectedRecords) => {
    let records = Array.isArray(selectedRecords) ? selectedRecords : [selectedRecords];
    const firstRecord = __.cloneDeep(records[0]);
    records = __.filter(records, {airportCode: firstRecord.airportCode, companyUuid: firstRecord.companyUuid});

    let defaults = {};
    if ( !defaultValues.defaultTemplate || !defaultValues.defaultAttachment) {
      defaults = await getDefaultValues(SystemData, "handlingorders", "Handling Order");
      if (!defaults)
        return;
    }
    let lines = [];
    records.forEach(record => {
      const legType = record.legType;
      const line =
        {
          tripLegUuid: record.tripLegUuid,
          airportCode: record.airportCode,
          direction: record.direction,
          scheduledTimeUTC: record.direction === "IN" ? record.scheduledArrivalTimeUTC : record.scheduledDepartureTimeUTC,
          reference: `${record.airlineCode}${record.flightNo}-${moment.utc(record.scheduledDepartureTimeUTC).format("MMDD")}`,
          locationAirportCode: record.originAirportCode,
          originAirportCode: record.originAirportCode,
          destinationAirportCode: record.destinationAirportCode,
          airlineCode: record.airlineCode,
          flightNo: record.flightNo,
          flightNumber: `${record.airlineCode}${record.flightNo}`,
          aircraftType: "B747F",
          aircraftTailNumber: record.aircraftReg,
          arrivalDate: moment.utc(record.scheduledArrivalTimeUTC).format("YYYY-MM-DDT00:00:00.000Z"),
          scheduledArrivalTimeUTC: moment.utc(record.scheduledArrivalTimeUTC).format("HH:mm"),
          departureDate: moment.utc(record.scheduledDepartureTimeUTC).format("YYYY-MM-DDT00:00:00.000Z"),
          scheduledDepartureTimeUTC: moment.utc(record.scheduledDepartureTimeUTC).format("HH:mm"),
          legType,
          cargo: legType === "FERRY" ? "Empty Ferry" : legType === "FULL CHARTER" ? "100 Metric Tonnes" : legType === "PART CHARTER" ? "50 Metric Tonnes" : "",
          orderUuid: record.orderUuid,
          serviceProviderUuid: record.serviceProviderUuid,
          companyName: record.companyName
        }
        lines.push(line);
    });

    const preSelectedServices = await getPreSelectedServices();
    const {companyUuid, handlingOrderTemplateUuid} = await getSupplierAndTemplate(firstRecord);
    const requiredServices = await getRequiredServices(firstRecord, companyUuid);
    if (preSelectedServices && Array.isArray(preSelectedServices)) {
      preSelectedServices.forEach(serviceCode => {
        if (!requiredServices.includes(serviceCode))
          requiredServices.push(serviceCode);
      });
    }
    axios.put(`${apiSitePrefix}/handlingorders/getPayloadData`, {lines})
    .then (response => {
      if (!response || !response.data || !Array.isArray(response.data) || response.data.length < 1) {
        SystemData.alert("Error", `An unexpected error occurred preparing handling order selection`);
        return;
      }
      const lines = response.data;

      const newOrderTemplate = {
        handlingOrderUuid: uuidv4(),
        airportCode: firstRecord.airportCode,
        orderDate: moment().format("YYYY-MM-DD HH:mm:ss"),
        supplierUuid: companyUuid,
        requiredServices,
        lines,
        status: "REQUIRED",
        tripNo: null,
        handlingOrderTemplateUuid: handlingOrderTemplateUuid || defaults.defaultTemplate,
        attachmentUuid: defaults.defaultAttachment,
        companyName: firstRecord.companyName
      }
      setAddDialogStates({...addDialogStates, showDialog: true, record: {...(__.cloneDeep(newOrderTemplate))}, parentRecord: null, title: 'New Handling Order'});
    })
    .catch (() => {
      SystemData.alert("Error", `An unexpected error occurred preparing handling order selection`);
    });
  }

  const handleRecommendedActions = async (cancelLines, amendLines) => {
    const firstRecord = __.cloneDeep(cancelLines[0] || amendLines[0]);
    firstRecord["type"] = "notification";
    firstRecord["supplierUuid"] = firstRecord.companyUuid;
    const subject = `Magma Aviation Service Changes Notification // ${firstRecord.airportCode}`;
    if (firstRecord.cancellationTemplate === undefined || !isHTML(firstRecord.cancellationTemplate)) {
      const template = await getTemplate(SystemData, "handlingorders", "Handling Order Cancellation", "cancellation", firstRecord.cancellationTemplate);
      firstRecord["orderTemplate"] = template;
    }

    let defaults = {};
    if ( !defaultValues.defaultTemplate || !defaultValues.defaultAttachment) {
      defaults = await getDefaultValues(SystemData, "handlingorders", "Handling Order");
      if (!defaults)
        return;
    }
    let amendRecords = [];
    amendLines.forEach(record => {
      const line =
        {
          airlineCode: record.orderAirlineCode,
          flightNo: record.orderFlightNo,
          flightNumber: `${record.orderAirlineCode}${record.orderFlightNo}`,

          orderOriginAirportCode: record.orderOriginAirportCode,
          orderDestinationAirportCode: record.orderDestinationAirportCode,
          orderArrivalDate: moment.utc(record.orderScheduledArrivalTimeUTC).format("YYYY-MM-DDT00:00:00.000Z"),
          orderScheduledArrivalTimeUTC: moment.utc(record.orderScheduledArrivalTimeUTC).format("HH:mm"),
          orderDepartureDate: moment.utc(record.orderScheduledDepartureTimeUTC).format("YYYY-MM-DDT00:00:00.000Z"),
          orderScheduledDepartureTimeUTC: moment.utc(record.orderScheduledDepartureTimeUTC).format("HH:mm"),

          originAirportCode: record.originAirportCode,
          destinationAirportCode: record.destinationAirportCode,
          arrivalDate: moment.utc(record.scheduledArrivalTimeUTC).format("YYYY-MM-DDT00:00:00.000Z"),
          scheduledArrivalTimeUTC: moment.utc(record.scheduledArrivalTimeUTC).format("HH:mm"),
          departureDate: moment.utc(record.scheduledDepartureTimeUTC).format("YYYY-MM-DDT00:00:00.000Z"),
          scheduledDepartureTimeUTC: moment.utc(record.scheduledDepartureTimeUTC).format("HH:mm"),
          details: record.reasonText,

          airportCode: record.airportCode,
          tripLegUuid: record.tripLegUuid,
          direction: record.direction,
          orderUuid: record.orderUuid
        }
        amendRecords.push(line);
    });

    let cancelRecords = [];
    cancelLines.forEach(record => {
      const line =
      {
        airlineCode: record.orderAirlineCode,
        flightNo: record.orderFlightNo,
        flightNumber: `${record.orderAirlineCode}${record.orderFlightNo}`,

        orderOriginAirportCode: record.orderOriginAirportCode,
        orderDestinationAirportCode: record.orderDestinationAirportCode,
        orderArrivalDate: moment.utc(record.orderScheduledArrivalTimeUTC).format("YYYY-MM-DDT00:00:00.000Z"),
        orderScheduledArrivalTimeUTC: moment.utc(record.orderScheduledArrivalTimeUTC).format("HH:mm"),
        orderDepartureDate: moment.utc(record.orderScheduledDepartureTimeUTC).format("YYYY-MM-DDT00:00:00.000Z"),
        orderScheduledDepartureTimeUTC: moment.utc(record.orderScheduledDepartureTimeUTC).format("HH:mm"),

        airportCode: record.airportCode,
        direction: record.direction,
        tripLegUuid: record.tripLegUuid,
        orderUuid: record.orderUuid
      };
      cancelRecords.push(line);
    });

    firstRecord["lines"] = [amendRecords, cancelRecords];
    if (firstRecord.orderTemplate) {
      const newTemplate = {
        ...firstRecord,
        orderDate: moment().format("YYYY-MM-DD HH:mm:ss"),
        orderSubject: subject,
        carbonCopy: defaultValues && __.has(defaultValues, "ccList") ? defaultValues.ccList : defaults.ccList,
      }
      const title = `Send Change Notification${firstRecord ? " to '" + firstRecord.companyName + "'":""}${firstRecord.airportCode ? " at '" + firstRecord.airportCode + "'": ""}`;
      setSendDialogStates({...sendDialogStates, showDialog: true, record: {...(__.cloneDeep(newTemplate))}, parentRecord: null, title: title});
    }

  }

  const handleApplyActionsClick = async (selectedRecords) => {
    const cancelRequirmentLines = __.filter(selectedRecords, {recommendedActionCode: 'CANCEL REQUIREMENT'});
    const amendRequiredLines = __.filter(selectedRecords, {recommendedActionCode: 'AMEND REQUIRED ORDER'});
    const amendLines = __.filter(selectedRecords, {recommendedActionCode: 'AMEND ORDER'});
    const cancelLines = __.filter(selectedRecords, {recommendedActionCode: 'CANCEL ORDER'});

    if (Array.isArray(cancelRequirmentLines) && cancelRequirmentLines.length > 0) {
      await handleCancelRequirement(cancelRequirmentLines);
    }

    if (Array.isArray(amendRequiredLines) && amendRequiredLines.length > 0) {
      await handleAmendOrder(amendRequiredLines);
    }

    //Create a combined email
    if ((Array.isArray(amendLines) && amendLines.length > 0) || (Array.isArray(cancelLines) && cancelLines.length > 0)) {
      await handleRecommendedActions(cancelLines, amendLines);
    }

  }

  const getOrderPeriod = (lines) => {
    let periodFrom = null;
    let periodTo = null;
    if (lines && Array.isArray(lines)) {
      lines.forEach(line => {
        if (periodFrom === null || line.departureDate < periodFrom) {
          periodFrom = line.departureDate;
        }
        if (periodTo === null || line.departureDate > periodTo) {
          periodTo = line.departureDate;
        }
      });
    }
    return({periodFrom, periodTo});
  }

  const handleSendOrderClick = async (rowData) => {
    let defaults = {};
    if ( !defaultValues.defaultTemplate || !defaultValues.defaultAttachment) {
      defaults = await getDefaultValues(SystemData, "handlingorders", "HandlingOrder");
      setDefaultValues(defaults);
    }

    const {periodFrom, periodTo} = getOrderPeriod(rowData.lines);
    const month = periodFrom && periodTo ? moment(periodFrom, "YYYY-MM-DD") : moment();
    const orderSubject = `Magma Aviation Handling Request // ${rowData.airportCode} // ${month.format("MMM-YYYY")}`;
    if (rowData.orderTemplate === undefined) {
      const template = await getTemplate(SystemData, "handlingorders", "Handling Order", "order",  rowData.fuelOrderTemplateUuid);
      rowData["orderTemplate"] = template;
    }
    if (rowData.orderTemplate) {
      const newFuelOrderTemplate = {
        ...rowData,
        orderDate: moment().format("YYYY-MM-DD HH:mm:ss"),
        orderSubject: orderSubject,
        carbonCopy: defaultValues && __.has(defaultValues, "ccList") ? defaultValues.ccList : defaults.ccList,
      }
      const title = `Send Handling Order${rowData.supplierName ? " to '" + rowData.supplierName + "'":""}${rowData.airportCode ? " at '" + rowData.airportCode + "'": ""}`;
      setSendDialogStates({...sendDialogStates, showDialog: true, record: {...(__.cloneDeep(newFuelOrderTemplate))}, parentRecord: null, title: title});
    }
  }

  const closeSendHandlingOrder = () => {
    setSendDialogStates({
      ...addDialogStates,
      showDialog: false,
      record: {},
      parentRecord: null,
      title: 'Send Handling Order'
    });
  }

  const selectionAllowed = (rowData) => {
    if (tableRef.current) {
      const data = tableRef.current.dataManager.data;
      if (data && Array.isArray(data)) {
        let selectedAirport = null;
        let companyUuid = null;
        let recommendedActionCode = null;
        for (let i = 0; i < data.length; i++) {
          const item = data[i];
          if (__.has(item.tableData, "checked") && item.tableData.checked) {
            selectedAirport = item.airportCode;
            companyUuid = item.companyUuid;
            recommendedActionCode = (item.recommendedActionCode==='AMEND ORDER'||item.recommendedActionCode==='CANCEL ORDER'?'NOTIFY':item.recommendedActionCode);
            break;
          }
        }
        if (selectedAirport) {
          return (rowData.airportCode === selectedAirport && rowData.companyUuid === companyUuid && recommendedActionCode === (rowData.recommendedActionCode==='AMEND ORDER'||rowData.recommendedActionCode==='CANCEL ORDER'?'NOTIFY':rowData.recommendedActionCode));
        }
      }
      return true;
    }
  }

  const getColumns = (props) => {
    const validatorColumn = ValidatorColumn(HandlingOrdersValidator, true);

    const baseColumn = {
      cellStyle: { fontSize:12, padding: "4px 2px" },
      headerStyle: { padding: "4px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false
    };

    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...(ColumnWidth(110, [10,10], undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "At Airport",
        field: "airportCode",
        initialEditValue: "",
        sorting: true,
        editable: "never",
        render: (rowData) => (
          <LightTooltip title={<AirportNameTooltip airportCode={rowData.airportCode} />}>
            <span> {rowData.airportCode} </span>
          </LightTooltip>
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(120,[10,10],undefined,undefined,undefined,undefined,false),
        title: "Flight Number",
        field: "orderFlightNo",
        render: (props) => (
          <div style={{ width: 162 }}>
            {props.orderAirlineCode}&nbsp;{props.orderFlightNo}
          </div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100, undefined, undefined, undefined, undefined)),
        title: "Flight Date",
        field: "scheduledTimeUTC",
        editable: "never",
        customFilterAndSearch: (term, rowData) => {
          const searchDate = moment.utc(rowData.scheduledTimeUTC).startOf('day').format('DD MMM YYYY').toUpperCase();
          const termUpper = `${term}`.toUpperCase();
          return searchDate.indexOf(termUpper) !== -1
        },
        render: rowData => {
          const displayDate = rowData.scheduledTimeUTC ? moment(rowData.scheduledTimeUTC).format(dateFormat) : "Not Sent Yet";
          return (<div>{displayDate}</div>);
        },
      },
      {
        ...baseColumn,
        ...(ColumnWidth(160, undefined, undefined, undefined, undefined)),
        title: "Scheduled Time",
        field: "scheduledTimeUTC",
        render: rowData => {
          const scheduledTime = rowData.scheduledTimeUTC;
          return (
            <div>{scheduledTime ? moment(scheduledTime).format("HH:mm") : ""} {rowData.direction === "OUT" ? `(Outbound ${rowData.airportCode})` : `(Inbound ${rowData.airportCode})`}</div>
          )
        }
      },
      {
        ...baseColumn,
        ...(ColumnWidth(130, undefined, undefined, undefined, undefined)),
        title: "Order Created On",
        field: "orderDate",
        render: rowData => {
          const displayDate = rowData.orderDate ? moment(rowData.orderDate).format(dateFormat) : "Not Sent Yet";
          return (<div>{displayDate}</div>);
        },
      },
      {
        ...baseColumn,
        ...(ColumnWidth(180, undefined, undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "Original Order Status",
        field: "status",
        editable: "never",
        emptyValue: "",
        fullWidth: true,
        cellStyle: (value, rowData) => {
          const backgroundColor = getStatusColour(backgroundColours, value);
          const color = getStatusColour(colours, value);
          const {cellStyle} = ColumnWidth(100);
          return ({
            ...cellStyle,
            color,
            backgroundColor,
            textAlign: "center",
            border: `1px solid #d0d0d0`,
            fontWeight: 600
          });
        },
        render: rowData => (
          <ComboSelect
            group="HANDLING ORDER STATUS"
            subGroup=""
            value={rowData.status}
            displayOnly={true}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(300, undefined, undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "Ordered From",
        field: "companyUuid",
        emptyValue: null,
        initialEditValue: null,
        render: (rowData) => (
          <CompanyUuidLookup
            value={rowData.companyUuid}
            type="render"
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(undefined, undefined, undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "Reason",
        field: "reason",
        editable: "never",
        emptyValue: "",
        fullWidth: true,
        render: rowData => (
          <ComboSelect
            group="SERVICE CANCELLATION REASONS"
            subGroup=""
            value={rowData.reason}
            displayOnly={true}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(undefined, undefined, undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "Recommended Action",
        field: "recommendedActionText",
        editable: "never",
        emptyValue: "",
        fullWidth: true,
      },
    ];
  };

  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
      <MaterialTable
        tableRef={tableRef}
        style={{
          border: "none",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          addRowPosition: "first",
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.tableHeader.backgroundColor,
            color: theme.palette.tableHeader.textColor,
            padding: "2px",
          },
          toolbarButtonAlignment: "right",
          pageSize: 15,
          detailPanelType: "single",
          padding: "dense",
          selection: state.canEdit,
          selectionProps: rowData => ({
            disabled: !selectionAllowed(rowData),
            color: "primary"
          })
        }}
        components={{
          ...defaultComponents,
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
          Action: _props => {
            let props = {..._props};
            if (typeof props.action.action === "function") {
              props.action = {..._props.action, ..._props.action.action(_props.data)};
            }
            if (props.action.icon==="generate") {
              return (
                <Tooltip title={props.action.tooltip}>
                  <Button
                    style={{...props.action.style, height: 24, display: props.action.hidden?'none':'inherit'}}
                    variant="contained"
                    color="primary"
                    startIcon={props.action.startIcon||<PlayIcon/>}
                    className={classes.button}
                    disabled={ props.action.disableForStatus?props.action.disableForStatus.includes(props.data[0].status)||props.action.disabled:props.action.disabled}
                    onClick={() => {
                      props.action.onClick(props.data)
                    }}
                  >
                    {props.action.text}
                  </Button>
                </Tooltip>
              )
            } else if (props.action.icon) {
              if (props.action.tooltip) {
                return (<Tooltip title={props.action.tooltip}><IconButton {...__.pick(props.action, ['onClick', 'style', 'color', 'disabled'])}><Icon>{props.action.icon}</Icon></IconButton></Tooltip>)
              }
              return (<IconButton {...__.pick(props.action, ['onClick', 'style', 'color', 'disabled'])}><Icon>{props.action.icon}</Icon></IconButton>)
            } else {
              return (<></>)
            }
          }
        }}
        actions={[
          {
            icon: 'refresh',
            tooltip: "Refresh List",
            isFreeAction: true,
            onClick: (e) => {
              refreshList();
            }
          },
          {position: "row",
           action: (rowData) => {
            const selectedCount = tableRef?.current?.dataManager?.selectedCount;
            return ({
              style: {width: 150},
              icon: "generate",
              text: "Ignore Change",
              tooltip: "Ignore changes made to the flight criteria since ordering.",
              onClick: handleIgnoreChangesClick,
              startIcon: <Icon>redo</Icon>,
              hidden: ['ORIGIN', 'DESTINATION', 'DEPARTURE TIME', 'ARRIVAL TIME'].indexOf(`${rowData.reason}`) < 0,
              disabled: !state.canEdit || selectedCount > 0,
            })},
          },
          (rowData) => ({
            icon: "generate",
            text: "Apply Recommended Action(s)",
            tooltip: "Create a new amended order",
            startIcon: <Icon>edit</Icon>,
            onClick: handleApplyActionsClick,
            hidden: !rowData[0].reason
          }),
        ]}
        title="Handling Orders to Review"
        columns={getColumns({canEdit: state.canEdit})}
        data={handlingOrders.data}
        editable={{
          isEditable: () => {
            return state.canEdit;
          },
          isDeletable: () => {
            return state.canEdit;
          },
          onRowAdd: undefined,
          onRowUpdate: undefined,
          onRowDelete: undefined,
        }}
      />
      {addDialogStates.showDialog
        ?
          <AddHandlingOrderDialog
            dialogStates={addDialogStates}
            canEdit={state.canEdit}
            SystemData={SystemData}
            extraKeys={extraKeys}
            handleAdd={handleMoveOrderLinesToNewHandlingOrder}
            formatChars={formatChars}
            classes={classes}
            close={()=>closeAddOrder()}
            onChange={(record)=>{
              closeAddOrder();
              handleSendOrderClick(record);
            }}
          />
        :
          <></>
      }
      {sendDialogStates.showDialog
        ?
          <SendHandlingOrderDialog
            dialogStates={sendDialogStates}
            canEdit={state.canEdit}
            SystemData={SystemData}
            extraKeys={extraKeys}
            formatChars={formatChars}
            classes={classes}
            close={()=>closeSendHandlingOrder()}
          />
        :
          <></>
      }

    </div>
  );

}

export default HandlingOrdersToReviewList;
